/**
 * Initialise Tako Text Input
 */

/* -----------------------------------------------------------------------------
 * Tako Text Input
 * -------------------------------------------------------------------------- */
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';
import { takoUUID } from './utils/takoUUID';
import { showElement } from './utils/showElement';
import { hideElement } from './utils/hideElement';

const COMPONENT_NAME = 'TextInput';

export const init = (unresolvedScope: string | HTMLElement | unknown) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    // Auto-inject password toggle buttons for password inputs
    const passwordInputsForInjection = scope.querySelectorAll<HTMLInputElement>(
        '.tako-form-control--password',
    );

    passwordInputsForInjection.forEach((input) => {
        try {
            if (!input.id) {
                input.id = takoUUID('text-input');
            }

            const wrapper = input.closest('.tako-form-control__wrapper');
            if (!wrapper) {
                return;
            }

            const existingToggle = wrapper.querySelector<HTMLElement>(
                `.tako-form-control__password-toggle[data-input-id="${input.id}"]`,
            );
            if (existingToggle) {
                return;
            }

            const showLabel =
                input.getAttribute('data-i18n-show-password-label') ||
                'Show password';

            const toggleBtn = document.createElement('button');
            toggleBtn.type = 'button';
            toggleBtn.className =
                'tako-form-control__action-button tako-form-control__password-toggle tako-button tako-button--ghost tako-button--secondary tako-button--icon tako-button--small';
            toggleBtn.setAttribute('data-input-id', input.id);
            toggleBtn.setAttribute('aria-label', showLabel);
            toggleBtn.setAttribute('title', showLabel);

            const iconSpan = document.createElement('span');
            iconSpan.className = 'tako-icon tako-icon--sm';
            const icon = document.createElement('i');
            icon.className = 'far fa-eye';
            icon.setAttribute('aria-hidden', 'true');
            iconSpan.appendChild(icon);
            toggleBtn.appendChild(iconSpan);

            wrapper.appendChild(toggleBtn);
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: input,
            });
        }
    });

    // Initialize password toggle functionality
    const passwordToggles = scope.querySelectorAll<HTMLElement>(
        '.tako-form-control__password-toggle',
    );

    passwordToggles.forEach((toggle) => {
        try {
            toggle.addEventListener(
                'click',
                function (this: HTMLElement, e: Event) {
                    e.preventDefault();

                    const inputId = this.getAttribute('data-input-id');
                    const input = scope.querySelector<HTMLInputElement>(
                        `#${inputId}`,
                    );
                    const icon = this.querySelector<HTMLElement>('i');

                    if (!input || !icon) {
                        return;
                    }

                    // Toggle input type
                    if (input.type === 'password') {
                        input.type = 'text';
                        icon.className = 'far fa-eye-slash';
                        const hideLabel =
                            input.getAttribute(
                                'data-i18n-hide-password-label',
                            ) || 'Hide password';
                        this.setAttribute('aria-label', hideLabel);
                        this.setAttribute('title', hideLabel);
                    } else {
                        input.type = 'password';
                        icon.className = 'far fa-eye';
                        const showLabel =
                            input.getAttribute(
                                'data-i18n-show-password-label',
                            ) || 'Show password';
                        this.setAttribute('aria-label', showLabel);
                        this.setAttribute('title', showLabel);
                    }
                },
            );
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: toggle,
            });
        }
    });

    // Initialize clear functionality for any clearable input
    // First, auto-inject clear buttons for inputs marked as clearable
    const clearableInputsForInjection =
        scope.querySelectorAll<HTMLInputElement>(
            '.tako-form-control--clearable',
        );

    clearableInputsForInjection.forEach((input) => {
        try {
            // Ensure input has an ID; generate one if missing
            if (!input.id) {
                input.id = takoUUID('text-input');
            }

            const wrapper = input.closest('.tako-form-control__wrapper');
            if (!wrapper) {
                return;
            }

            // If a clear button already exists for this input, skip injection
            const existingButton = wrapper.querySelector<HTMLElement>(
                `.tako-form-control__clear-button[data-input-id="${input.id}"]`,
            );
            if (existingButton) {
                return;
            }

            // Read i18n label from data attribute on input, fallback to default
            const i18nLabel =
                input.getAttribute('data-i18n-clear-label') || 'Clear input';

            // Create clear button element
            const button = document.createElement('button');
            button.type = 'button';
            button.className =
                'tako-form-control__clear-button tako-button tako-button--ghost tako-button--secondary tako-button--icon tako-button--small';
            button.setAttribute('data-input-id', input.id);
            button.setAttribute('aria-label', i18nLabel);
            button.setAttribute('title', i18nLabel);

            // Icon
            const iconSpan = document.createElement('span');
            iconSpan.className = 'tako-icon tako-icon--sm';
            const icon = document.createElement('i');
            icon.className = 'far fa-xmark';
            icon.setAttribute('aria-hidden', 'true');
            iconSpan.appendChild(icon);
            button.appendChild(iconSpan);

            // Inject button after input inside wrapper
            wrapper.appendChild(button);
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: input,
            });
        }
    });

    const clearButtons = scope.querySelectorAll<HTMLElement>(
        '.tako-form-control__clear-button',
    );

    clearButtons.forEach((clearBtn) => {
        try {
            clearBtn.addEventListener(
                'click',
                function (this: HTMLElement, e: Event) {
                    e.preventDefault();

                    const inputId = this.getAttribute('data-input-id');
                    const input = scope.querySelector<HTMLInputElement>(
                        `#${inputId}`,
                    );

                    if (!input) {
                        return;
                    }

                    // Clear the input value
                    input.value = '';

                    // Focus back on the input
                    input.focus();

                    // Trigger input event for any listeners
                    const event = new Event('input', { bubbles: true });
                    input.dispatchEvent(event);

                    // Hide the clear button
                    hideElement(this);

                    // Trigger custom clear event for controlled scenarios
                    const clearEvent = new CustomEvent('tako:clear', {
                        bubbles: true,
                        detail: { inputId },
                    });
                    input.dispatchEvent(clearEvent);
                },
            );
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: clearBtn,
            });
        }
    });

    // Initialize show/hide clear button for clearable inputs
    const clearableInputs = scope.querySelectorAll<HTMLInputElement>(
        '.tako-form-control--clearable',
    );

    clearableInputs.forEach((input) => {
        try {
            const wrapper = input.closest('.tako-form-control__wrapper');
            if (!wrapper) {
                return;
            }

            const clearBtn = wrapper.querySelector<HTMLElement>(
                '.tako-form-control__clear-button',
            );
            if (!clearBtn) {
                return;
            }

            // Function to toggle clear button visibility
            const toggleClearButton = () => {
                if (input.value.trim() === '') {
                    hideElement(clearBtn);
                } else {
                    showElement(clearBtn, 'flex');
                }
            };

            // Initial state
            toggleClearButton();

            // Listen for input changes
            ['input', 'keyup', 'paste'].forEach((eventType) => {
                input.addEventListener(eventType, function () {
                    if (eventType === 'paste') {
                        // Use setTimeout to ensure paste content is processed
                        setTimeout(toggleClearButton, 10);
                    } else {
                        toggleClearButton();
                    }
                });
            });
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: input,
            });
        }
    });
};
